import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import axiosInstance from "api/axiosInstance";
import { API_ENDPOINTS } from "api/endpoints";
import { Rep, RepPayload, RepsState } from "types/rep";

type ApiResponse<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    if (!axiosError.response) {
      return "Network error. Please try again.";
    }

    return (
      axiosError.response.data?.message ||
      "Something went wrong. Please try again."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

export const fetchReps = createAsyncThunk<
  Rep[],
  void,
  { rejectValue: string }
>("reps/fetchReps", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ApiResponse<Rep[]>>(
      API_ENDPOINTS.reps.list
    );

    if (response.data.success === false) {
      return rejectWithValue(response.data.message || "Unable to fetch RAPs.");
    }

    return response.data.data || [];
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const createRep = createAsyncThunk<
  Rep,
  RepPayload,
  { rejectValue: string }
>("reps/createRep", async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ApiResponse<Rep>>(
      API_ENDPOINTS.reps.create,
      payload
    );

    if (response.data.success === false) {
      return rejectWithValue(response.data.message || "Unable to create RAP.");
    }

    if (!response.data.data) {
      return rejectWithValue("Unable to create RAP.");
    }

    return response.data.data;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const updateRep = createAsyncThunk<
  Rep,
  { id: number; payload: RepPayload },
  { rejectValue: string }
>("reps/updateRep", async ({ id, payload }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ApiResponse<Rep>>(
      API_ENDPOINTS.reps.update(id),
      payload
    );

    if (response.data.success === false) {
      return rejectWithValue(response.data.message || "Unable to update RAP.");
    }

    if (!response.data.data) {
      return rejectWithValue("Unable to update RAP.");
    }

    return response.data.data;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const deleteRep = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("reps/deleteRep", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete<ApiResponse<unknown>>(
      API_ENDPOINTS.reps.delete(id)
    );

    if (response.data?.success === false) {
      return rejectWithValue(response.data.message || "Unable to delete RAP.");
    }

    return id;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

const initialState: RepsState = {
  reps: [],
  loading: false,
  actionLoading: false,
  error: null,
};

const repsSlice = createSlice({
  name: "reps",
  initialState,
  reducers: {
    clearRepsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReps.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReps.fulfilled, (state, action: PayloadAction<Rep[]>) => {
        state.reps = action.payload;
        state.loading = false;
      })
      .addCase(fetchReps.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unable to fetch RAPs.";
      })
      .addCase(createRep.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createRep.fulfilled, (state, action) => {
        state.reps = [action.payload, ...state.reps];
        state.actionLoading = false;
      })
      .addCase(createRep.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to create RAP.";
      })
      .addCase(updateRep.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateRep.fulfilled, (state, action) => {
        state.reps = state.reps.map((rep) =>
          rep.id === action.payload.id ? action.payload : rep
        );
        state.actionLoading = false;
      })
      .addCase(updateRep.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to update RAP.";
      })
      .addCase(deleteRep.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deleteRep.fulfilled, (state, action) => {
        state.reps = state.reps.filter((rep) => rep.id !== action.payload);
        state.actionLoading = false;
      })
      .addCase(deleteRep.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to delete RAP.";
      });
  },
});

export const { clearRepsError } = repsSlice.actions;
export default repsSlice.reducer;
